import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useKeepInViewport } from '../hooks/useKeepInViewport';
import { useBoundaries } from '../hooks/useBoundaries';
import BoundaryDivider from './BoundaryDivider';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface MonthPickerProps {
  value: string;                 // "YYYY-MM"
  onChange: (value: string) => void;
  max?: string;                  // "YYYY-MM" inclusive upper bound (optional)
  className?: string;
}

export default function MonthPicker({ value, onChange, max, className }: MonthPickerProps) {
  const [open, setOpen] = useState(false);
  const [selYear, selMonth] = value.split('-').map((n) => parseInt(n, 10));
  const [viewYear, setViewYear] = useState<number>(selYear || new Date().getFullYear());
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useKeepInViewport(open); // stays inside a narrow screen
  const boundaries = useBoundaries();

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Escape closes it and puts focus back on the button, as for any popup.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const maxYear = max ? parseInt(max.split('-')[0], 10) : Infinity;
  const maxMonth = max ? parseInt(max.split('-')[1], 10) : 12;
  const isDisabled = (y: number, m: number) => !!max && (y > maxYear || (y === maxYear && m > maxMonth));

  // The months of the year in view, in calendar order; those before the Jira conventions started are a section of their own.
  const allMonths = MONTHS_SHORT.map((m, i) => ({ m, month: i + 1 }));
  const isEarlier = (month: number) => !!boundaries && `${viewYear}-${String(month).padStart(2, '0')}` < boundaries.month;
  const sections = [
    { earlier: true, list: allMonths.filter(({ month }) => isEarlier(month)) },
    { earlier: false, list: allMonths.filter(({ month }) => !isEarlier(month)) },
  ].filter((section) => section.list.length > 0);

  const label = value ? `${MONTHS_FULL[(selMonth || 1) - 1]} ${selYear}` : 'Select month';

  const toggle = () => {
    if (!open) setViewYear(selYear || new Date().getFullYear());
    setOpen((o) => !o);
  };

  const pick = (m: number) => {
    if (isDisabled(viewYear, m)) return;
    onChange(`${viewYear}-${String(m).padStart(2, '0')}`);
    setOpen(false);
  };

  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100"
      >
        <Calendar className="h-4 w-4 text-gray-400" />
        {label}
      </button>

      {open && (
        <div ref={popRef} role="group" aria-label="Choose a month" className="absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-1rem)] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl ring-1 ring-black/5">
          {/* Year navigator */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
              aria-label="Previous year"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">{viewYear}</span>
            <button
              type="button"
              disabled={viewYear >= maxYear}
              onClick={() => setViewYear((y) => y + 1)}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Next year"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Month grid: the months before the Jira conventions started sit apart, under their own caption. */}
          {sections.map(({ earlier, list }) => {
            return (
              <div key={earlier ? 'before' : 'from'}>
                {boundaries && (earlier || sections.length > 1) && <BoundaryDivider label={earlier ? boundaries.label : boundaries.fromLabel} />}
                <div className="grid grid-cols-3 gap-1.5">
                  {list.map(({ m, month }) => {
                    const selected = viewYear === selYear && month === selMonth;
                    const disabled = isDisabled(viewYear, month);
                    return (
                      <button
                        key={month}
                        type="button"
                        disabled={disabled}
                        onClick={() => pick(month)}
                        className={`rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-blue-600 text-white shadow-sm'
                            : disabled
                            ? 'cursor-not-allowed text-gray-300'
                            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
